import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, RefreshCw } from "lucide-react";
import { api, errorText, formatAddress, formatPhone } from "../lib/api";
import { STATUSES, STATUS_COLORS, SERVICES } from "../data/services";
import { STATUS_LABELS, SERVICE_LABELS, fmtDate } from "./AuthContext";

const CARDS = [
  ["new", "New requests", ["new"], "admin-stat-pending"],
  ["awaiting", "Awaiting customer", ["contacted", "quoted"], "admin-stat-awaiting"],
  ["accepted", "Accepted", ["accepted"], "admin-stat-accepted"],
  ["driver_assigned", "Drivers assigned", ["driver_assigned"], "admin-stat-assigned"],
  ["en_route", "En route", ["en_route"], "admin-stat-en-route"],
  ["completed", "Completed", ["completed"], "admin-stat-completed"],
  ["cancelled", "Cancelled", ["cancelled"], "admin-stat-cancelled"],
];

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLORS[status]}`} data-testid="status-badge">{STATUS_LABELS[status]}</span>
);

const Filters = ({ f, setF }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
    <label className="relative lg:col-span-2">
      <span className="sr-only">Search</span>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
      <input className="tow-input pl-9" placeholder="Reference, customer, phone, address" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="admin-request-search-input" />
    </label>
    <select className="tow-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="admin-status-filter-select" aria-label="Status">
      <option value="">All statuses</option>
      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
    </select>
    <select className="tow-input" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="admin-service-filter-select" aria-label="Service">
      <option value="">All services</option>
      {SERVICES.map((s) => <option key={s.id} value={s.id}>{SERVICE_LABELS[s.id]}</option>)}
    </select>
    <input type="date" className="tow-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} aria-label="From date" data-testid="admin-date-from" />
    <input type="date" className="tow-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} aria-label="To date" data-testid="admin-date-to" />
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ q: "", status: "", service: "", date_from: "", date_to: "", sort: "newest" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [tick]);

  useEffect(() => {
    const h = setTimeout(() => {
      api.get("/admin/requests", { params: f }).then((r) => { setRows(r.data); setError(""); }).catch((e) => setError(errorText(e)));
    }, 300);
    return () => clearTimeout(h);
  }, [f, tick]);

  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl sm:text-4xl font-extrabold uppercase">Service requests</h1>
        <button type="button" onClick={() => setTick((x) => x + 1)} className="btn-ghost px-4 py-2 text-sm" data-testid="admin-refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
        {CARDS.map(([key, label, sts, tid]) => {
          const n = stats ? sts.reduce((a, s) => a + (stats[s] || 0), 0) : "—";
          const active = sts.length === 1 && f.status === sts[0];
          return (
            <button key={key} type="button" data-testid={tid} onClick={() => setF({ ...f, status: sts.length === 1 && !active ? sts[0] : "" })}
              className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-[#FACC15] bg-[#FACC15]/10" : "border-[#2E2E3A] bg-[#18181F] hover:border-gray-500"}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
              <p className="mt-2 font-display text-4xl font-extrabold">{n}</p>
            </button>
          );
        })}
      </div>
      <Filters f={f} setF={setF} />
      <div className="flex items-center gap-3 text-sm">
        <label htmlFor="sort" className="text-gray-400">Sort</label>
        <select id="sort" className="tow-input w-auto py-2" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} data-testid="admin-sort-select">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="updated">Recently updated</option>
          <option value="status">Status</option>
        </select>
      </div>
      {error && <p role="alert" className="text-red-400">{error}</p>}
      {!rows ? <Loader2 className="h-6 w-6 animate-spin text-[#FACC15]" /> : rows.length === 0 ? (
        <p className="panel p-8 text-gray-400" data-testid="admin-no-requests">No requests match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#2E2E3A]">
          <table className="w-full min-w-[860px] text-sm" data-testid="admin-requests-table">
            <thead className="bg-[#121216] text-left text-xs uppercase tracking-wide text-gray-400">
              <tr><th className="p-3">Reference</th><th className="p-3">Received</th><th className="p-3">Service</th><th className="p-3">Customer</th><th className="p-3">Pickup → Destination</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => nav(`/admin/requests/${r.id}`)} className="cursor-pointer border-t border-[#2E2E3A] hover:bg-white/[.03]" data-testid={`admin-request-row-${r.ref}`}>
                  <td className="p-3 font-mono font-semibold text-[#FACC15]">
                    <a href={`/admin/requests/${r.id}`} onClick={(e) => { e.preventDefault(); nav(`/admin/requests/${r.id}`); }}>{r.ref}</a>
                    {!r.vehicle?.safe_location && <span className="ml-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">UNSAFE</span>}
                  </td>
                  <td className="p-3 text-gray-300 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="p-3">{SERVICE_LABELS[r.service_id]}</td>
                  <td className="p-3"><p>{r.customer.name}</p><p className="font-mono text-xs text-gray-400">{formatPhone(r.customer.phone)}</p></td>
                  <td className="p-3 text-gray-300 max-w-xs"><p className="truncate">{formatAddress(r.pickup)}</p><p className="truncate text-xs text-gray-500">→ {r.destination ? formatAddress(r.destination) : r.needs_destination_help ? "Needs help choosing" : "On-site service"}</p></td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
